import { FormEvent, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Spinner from "../../components/shared/Spinner/Spinner";
import MainButton from "../../components/shared/MainButton/MainButton";
import { FaPlus } from "react-icons/fa";
import { BannerService } from "../../services/banner.service";
import {
  Button,
  message,
  Modal,
  Popconfirm,
  Table,
  TableProps,
  Upload,
  UploadProps,
} from "antd";
import { BannerType } from "../../interfaces/admin.interface";
import ShowImage from "../../components/shared/show-image/ShowImage";
import { UploadOutlined } from "@ant-design/icons";

interface ImageModalType {
  selectedImage: string;
  showImageModal: boolean;
}

const Banner = () => {
  // Get Banner Image and Loading State
  const [bannerImageData, setBannerImageData] = useState<BannerType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Show Banner Image Modal
  const [imageModal, setImageModal] = useState<ImageModalType>({
    selectedImage: "",
    showImageModal: false,
  });

  // Edit and Add Banner Image
  const [imageSetting, setImageSetting] = useState({
    isEditing: false,
    isModalVisible: false,
  });

  // Fayl seçimini saxlamaq üçün state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Banner Image
  const [singleBannerImage, setSingleBannerImage] = useState<BannerType>();

  // Upload Banner Image
  const props: UploadProps = {
    name: "file",
    beforeUpload: (file) => {
      setSelectedFile(file);
      return false;
    },
    onRemove: () => {
      setSelectedFile(null);
    },
    fileList: selectedFile ? [selectedFile as any] : [],
  };

  // Image to base64 format
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Banner Image Modal
  const handleModalClick = (type: "ADD" | "EDIT") => {
    switch (type) {
      case "ADD":
        setImageSetting({
          isEditing: false,
          isModalVisible: true,
        });
        return;
      case "EDIT":
        setImageSetting(() => ({ isEditing: true, isModalVisible: true }));
        return;
    }
  };

  // Banner Image Delete function
  const handleDeleteClick = async (id: number) => {
    try {
      await BannerService.deleteBannerImage(id);
      const filteredData = bannerImageData.filter(
        (image: BannerType) => image.id !== id
      );
      setBannerImageData(filteredData);
      message.success("Banner şəkil uğurla silindi!");
    } catch (error) {
      console.log(error);
      message.error("Banner şəkil ");
    } finally {
      setLoading(false);
    }
  };

  // Banner Image Modal Submit
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      message.warning("Zəhmət olmasa şəkil seçin!");
      return;
    }

    try {
      const base64Image = await fileToBase64(selectedFile);

      let res;

      if (imageSetting.isEditing && singleBannerImage) {
        // 🔹 Edit zamanı PUT (update)
        res = await BannerService.updateBannerImage(singleBannerImage.id, {
          image: base64Image,
        });
      } else {
        // 🔹 Əlavə zamanı POST (create)
        res = await BannerService.addedBannerImage({
          image: base64Image,
        });
      }

      if (res.status === 200) {
        message.success(
          imageSetting.isEditing
            ? "Banner şəkil uğurla redaktə olundu!"
            : "Banner şəkil əlavə olundu!"
        );
        setSelectedFile(null);
        setImageSetting({ isEditing: false, isModalVisible: false });
        fetchData();
      }
    } catch (err) {
      console.error(err);
      message.error(
        imageSetting.isEditing
          ? "Banner şəkil redaktə olunmadı!"
          : "Banner şəkil əlavə olunmadı!"
      );
    }
  };

  // Get Banner Image
  const fetchData = async () => {
    setLoading(true);
    try {
      const bannerImage = await BannerService.getBannerImage();

      if (bannerImage.status === 200) {
        if (Array.isArray(bannerImage.data)) {
          const dataWithKeys = bannerImage.data.map(
            (item: BannerType, key: number) => ({
              ...item,
              key: key + 1,
            })
          );
          setBannerImageData(dataWithKeys);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns: TableProps<BannerType>["columns"] = [
    {
      title: "№",
      dataIndex: "key",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Banner şəkil",
      dataIndex: "image",
      render: (_, record) => (
        <div
          style={{ cursor: "pointer" }}
          onClick={() => {
            setImageModal(() => ({
              selectedImage: record.image,
              showImageModal: true,
            }));
          }}
        >
          {record.image ? (
            <img
              style={{ width: "40px" }}
              src={record.image}
              alt="banner-image"
            />
          ) : null}
        </div>
      ),
    },
    {
      title: (
        <div className="settings">
          <i className="fa-solid fa-gear"></i>
        </div>
      ),
      key: "action",
      render: (_, record) => (
        <div className="settings_icons">
          <div
            className="edit"
            onClick={() => {
              handleModalClick("EDIT");
              setSingleBannerImage(record);
            }}
          >
            <i className="fa-solid fa-pen-to-square"></i>
          </div>
          <Popconfirm
            title="Bu bloqu siləcəyinizə əminsiniz?"
            onConfirm={() => handleDeleteClick(record.id)}
            onCancel={() => message.info("Silinmə əməliyyatı ləğv edildi.")}
            okText="Bəli"
            cancelText="Xeyr"
            placement="topRight"
          >
            <div className="delete">
              <i className="fa-solid fa-trash"></i>
            </div>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Banner</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Banner şəkillər</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={() => handleModalClick("ADD")}>
              <FaPlus size={20} />
            </MainButton>
            <div className="pages_table">
              <Table<BannerType>
                scroll={{ x: "100%" }}
                columns={columns}
                dataSource={bannerImageData}
                rowKey={(record) => record.id.toString()}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title={
          imageSetting.isEditing
            ? "Banner şəkil redaktə edin"
            : "Banner şəkil əlavə edin"
        }
        open={imageSetting.isModalVisible}
        onCancel={() =>
          setImageSetting((prev) => ({ ...prev, isModalVisible: false }))
        }
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          {/* 🟩 Əgər redaktə modundasansa və əvvəlki şəkil varsa — göstər */}
          {imageSetting.isEditing && singleBannerImage?.image && (
            <div
              style={{
                textAlign: "center",
                marginBottom: "16px",
              }}
            >
              <img
                src={singleBannerImage.image}
                alt="current-banner"
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  objectFit: "contain",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                }}
              />
              <p style={{ fontSize: "14px", color: "#888", marginTop: "6px" }}>
                Mövcud şəkil
              </p>
            </div>
          )}

          {/* Yeni şəkil yükləmə inputu */}
          <div className="form_input">
            <Upload {...props}>
              <Button icon={<UploadOutlined />}>
                {imageSetting.isEditing ? "Şəkli dəyişin" : "Yeni şəkil seçin"}
              </Button>
            </Upload>
          </div>

          <div>
            <MainButton full type="submit" className="form_add_btn">
              {imageSetting.isEditing ? "Redaktə edin" : "Əlavə edin"}
            </MainButton>
          </div>
        </form>
      </Modal>

      <ShowImage
        image={imageModal.selectedImage}
        setShowImagesModal={() =>
          setImageModal((prev: ImageModalType) => ({
            ...prev,
            showImageModal: false,
          }))
        }
        showImageModal={imageModal.showImageModal}
      />
    </div>
  );
};

export default Banner;
